import api from '@api';
import React, { FC, useState, useEffect } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import Select, { OnChangeType, OnInputChangeType, OptionType } from '../Select';
import translate from '@translate';
import { colors } from 'src/themes/default';

export type ProductOptionType = OptionType & {
    /** 0 = neaktivní (stejná vizuální logika jako tabulka produktů v inventáři) */
    ACTIVE?: number;
    /** 1 = smazaný produkt (červený text v dropdownu) */
    IS_DELETED?: number;
};

export type ProductReceivedType = {
    ID: number;
    SKU: string;
    NAME: string;
    EAN: string | null;
    ACTIVE?: number;
    IS_DELETED?: number;
};

export type OnChangeProductType = (value: ProductReceivedType | ProductReceivedType[], error: boolean) => void;

export type ProductTypesType = 'finalProduct' | 'packagingMaterial';

export type ProductExtendsDataType = 'DESCRIPTION' | 'EAN' | 'EXP_METHOD' | 'PRODUCT_TYPE' | 'INSERT_TYPE';

export interface ProductsSelectProps {
    value?: string | number;
    name: string;
    idKey: IdKeyType;
    error?: boolean;
    label?: string;
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    onChange?: OnChangeProductType;
    productTypes?: ProductTypesType[];
    extendsData?: ProductExtendsDataType[];
    withGlobalProducts?: boolean;
    customFirstOption?: ProductOptionType;
    options?: ProductOptionType[]; // Přidáno pro volitelné options přes props
}

type IdKeyType = 'SKU' | 'ID';

const LOAD_COUNT = 50;
const INIT_TOTAL_COUNT = 50;

const toOptionalNumber = (value: number | undefined | null): number | undefined | null => {
    if (value === undefined || value === null) {
        return value;
    }
    return Number(value);
};

const formatItem = (item: ProductReceivedType, idKey: IdKeyType): ProductOptionType => ({
    label: item.EAN ? `${item.SKU} | ${item.NAME} | ${item.EAN}` : `${item.SKU} | ${item.NAME}`,
    value: item[idKey],
    ACTIVE: toOptionalNumber(item.ACTIVE) ?? undefined,
    IS_DELETED: toOptionalNumber(item.IS_DELETED) ?? undefined,
});

/** Smazaný produkt červeně, neaktivní šedě — stejné barvy jako inventář. */
const productOptionStyle = (option: OptionType): SxProps<Theme> | null => {
    const product = option as ProductOptionType;
    if (product.IS_DELETED !== undefined && product.IS_DELETED !== null && Number(product.IS_DELETED) === 1) {
        return { color: colors.alert_O };
    }
    if (product.ACTIVE !== undefined && product.ACTIVE !== null && Number(product.ACTIVE) === 0) {
        return { color: colors.text_T3 };
    }
    return null;
};

/** Styl inputu vybraného produktu (červeně smazaný, šedě neaktivní); při psaní filtru bez stylu. */
export const getSelectedInputSx = (
    selectedItem: ProductOptionType | null,
    value: string | number | undefined,
    inputFilterText: string
): SxProps<Theme> | null => {
    if (!selectedItem || inputFilterText !== '' || value === undefined || value === null) {
        return null;
    }
    if (String(selectedItem.value) !== String(value)) {
        return null;
    }
    return productOptionStyle(selectedItem);
};

const ProductsSelect: FC<ProductsSelectProps> = (props) => {
    const [productOptions, setProductOptions] = useState<ProductOptionType[] | any[]>(props.options || []);
    const [productOptionsComplete, setProductOptionsComplete] = useState<ProductReceivedType[] | any[]>([]);
    const [focus, setFocus] = useState<boolean>(false);
    const [inputFilterText, setInputFilterText] = useState<string>('');
    const [loadCount, setLoadCount] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(INIT_TOTAL_COUNT);
    const [selectedItem, setSelectedItem] = useState<ProductOptionType | null>(null);

    useEffect(() => {
        if (focus && !props.options) {
            setLoadCount(0);
            setTotalCount(INIT_TOTAL_COUNT);
            setProductOptions([]);
            setProductOptionsComplete([]);
            loadMoreRows(0);
        }
    }, [inputFilterText, focus, props.options]);

    useEffect(() => {
        if (props.options) {
            setProductOptions(props.options);
        }
    }, [props.options])

    useEffect(() => {
        if (!props.options) {
            loadSelectItem();
        }
    }, [props.value, props.options]);

    // load selected product
    const loadSelectItem = async () => {
        if (!props.value) {
            setSelectedItem(null);
        } else if (props.customFirstOption && props.value === props.customFirstOption?.value) {
            setSelectedItem(props.customFirstOption);
        } else {
            const response: { data: { data: ProductReceivedType } } = await api(
                'post',
                'api/secure/products/autocomplete-item',
                {
                    id: String(props.value),
                    key: props.idKey,
                    withGlobalProducts: props.withGlobalProducts ? 1 : 0,
                }
            );

            setSelectedItem(formatItem(response.data.data, props.idKey));
        }
    };

    // load and set data to list
    const loadMoreRows = (index: number) => {
        if (focus && !props.options) {
            const page = Math.floor(index / LOAD_COUNT);
            loadPage(page);
        }
    };

    const loadPage = async (page: number) => {
        setLoadCount((page + 1) * LOAD_COUNT);
        const response: { data: { data: { data: [ProductReceivedType]; totalCount: number } } } = await api(
            'post',
            'api/secure/products/autocomplete-suggestion',
            {
                text: inputFilterText,
                page,
                items: LOAD_COUNT,
                productTypes: props.productTypes,
                extendsData: props.extendsData,
                withGlobalProducts: props.withGlobalProducts ? 1 : 0,
            }
        );
        const newData = response.data.data.data.map((item) => formatItem(item, props.idKey));

        setProductOptions((old) => {
            // if fetching other data via lazy loading , show all options otherwise only new data from api
            if(page > 0) {
                return [...old, ...newData];
            } else {
                return [...newData]
            }
        });
        setProductOptionsComplete((old) => {
            // if fetching other data via lazy loading , show all options otherwise only new data from api
            if(page > 0) {
                return [...old, ...response.data.data.data];
            } else {
                return [...response.data.data.data]
            }
        });
        setTotalCount(response.data.data.totalCount);
    };

    const onChange: OnChangeType = (value, error) => {
        if (value && props.onChange) {
            const newItem = productOptionsComplete.find((product) => product[props.idKey] === value);
            props.onChange(newItem || value, error);
        }
    };

    const onItemsRendered = (params: {
        overscanStartIndex: number;
        overscanStopIndex: number;
        visibleStartIndex: number;
        visibleStopIndex: number;
    }) => {
        if (params.overscanStopIndex + 10 > loadCount && totalCount > loadCount && !props.options) {
            loadMoreRows(loadCount);
        }
    };

    const onInputChange: OnInputChangeType = (event, value, reason) => {
        switch (reason) {
            case 'input':
                setInputFilterText(value);
                break;
            case 'reset':
                setInputFilterText('');
                break;
            default:
                break;
        }
    };

    const onFocus = () => {
        setFocus(true);
    };
    const onBlur = () => {
        setFocus(false);
    };

    const getAllOptions = () => {
        const allProductOptions = [...productOptions];
        if(props.customFirstOption ){
            allProductOptions.unshift(props.customFirstOption)
        }
        if (selectedItem) {
            if (allProductOptions.find((option) => option.value === selectedItem.value)) {
                return allProductOptions;
            } else {
                return [selectedItem, ...allProductOptions];
            }
        }
        return allProductOptions;
    };

    const value = focus ? undefined : props.value;

    return (
        <Select
            options={getAllOptions()}
            value={value}
            error={props.error}
            name={props.name}
            label={translate('productsSelect.label')}
            fullWidth={props.fullWidth}
            disabled={props.disabled}
            required={props.required}
            onChange={onChange}
            onItemsRendered={onItemsRendered}
            onInputChange={onInputChange}
            onFocus={onFocus}
            onBlur={onBlur}
            virtualized
            minWidth={200}
            optionStyleFce={productOptionStyle}
            inputSx={getSelectedInputSx(selectedItem, props.value, inputFilterText)}
        />
    );
};

export default ProductsSelect;
